import {
  Button,
  ColumnType,
  DatePicker,
  Input,
  Loading,
  Pagination,
  Select,
  Table,
} from "@/components";
import DateRangePicker from "@/components/DateRangePicker";
import {
  FormCheckboxGroup,
  FormDatePicker,
  FormInput,
} from "@/components/Form";
import { Modal } from "@/components/modal/Modal";
import { monthlyCheckService } from "@/services/monthlyCheckService";
import { useDebounce } from "@/hooks/useDebounce";
import { ApiResponse } from "@/types";
import { formatToJapaneseDate } from "@/utils";
import { toastError } from "@/utils/toastify";
import { endOfDay, format } from "date-fns";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { HiDownload, HiOutlineSearch } from "react-icons/hi";
import { useSearchParams } from "react-router-dom";
import { CheckboxGroupOption } from "@/components/CheckboxGroup";
import { zodResolver } from "@hookform/resolvers/zod";
import { MonthlyInspectionDetailSchema } from "../monthly-inspection/schemas";

type MonthlyCheckStoreResultDetail = {
  id: number;
  store: {
    id: number;
    name: string;
    address?: string;
  };
  inspection: {
    id: number;
    theme: string;
    description: string;
  };
  templates_with_answers: {
    id: number;
    question: string;
    comment: string;
    store_answer: boolean | null;
    order: number;
  }[];
  admin_comment: string | null;
  answer_date: string | null;
  has_answered: boolean;
};

const KEYS = {
  PAGE: "page",
  SEARCH: "search",
  ANSWER_STATUS: "answer_status",
  FROM_DATE: "answer_date_from",
  END_DATE: "answer_date_to",
  TARGET_MONTH: "target_month",
};

const ANSWER_STATUS_OPTIONS = [
  { label: "回答済み", value: "回答済み" },
  { label: "未回答", value: "未回答" },
];

const ANSWER_STATUS_STYLE: Record<string, string> = {
  未回答: "bg-grey-600 size-2 rounded-full",
  回答済み: "bg-primary_light size-2 rounded-full",
};

const CalendarIcon = (
  <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
    <path
      d="M14 11H9V16H14V11ZM13 0V2H5V0H3V2H2C0.89 2 0.00999999 2.9 0.00999999 4L0 18C0 19.1 0.89 20 2 20H16C17.1 20 18 19.1 18 18V4C18 2.9 17.1 2 16 2H15V0H13ZM16 18H2V7H16V18Z"
      fill="black"
      fillOpacity="0.56"
    />
  </svg>
);

const parseDate = (value?: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
};

const getMonthlyTarget = (record: {
  applicable_month?: number | null;
  applicable_date?: string | null;
}) => {
  const month =
    record.applicable_month ||
    (record.applicable_date
      ? new Date(record.applicable_date).getMonth() + 1
      : null);

  if (!month) return "-";

  const year = record.applicable_date
    ? new Date(record.applicable_date).getFullYear()
    : null;

  return year ? `${year}年${month}月` : `${month}月`;
};

const buildListParams = (
  currentPage: number,
  debouncedSearch: string,
  params: Record<string, string>,
  targetMonthDate?: Date,
) => ({
  page: currentPage,
  limit: 14,
  isPagination: 1 as const,
  search: debouncedSearch || undefined,
  answer_status: params[KEYS.ANSWER_STATUS] || undefined,
  answer_date_from: params[KEYS.FROM_DATE] || undefined,
  answer_date_to: params[KEYS.END_DATE] || undefined,
  applicable_month: targetMonthDate
    ? targetMonthDate.getMonth() + 1
    : undefined,
  year: targetMonthDate ? targetMonthDate.getFullYear() : undefined,
  order: "DESC" as const,
  orderBy: "id" as const,
});

const MonthlyCheckList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = Object.fromEntries([...searchParams]);
  const isInitialMount = useRef(true);

  const currentPage = Number(params[KEYS.PAGE]) || 1;
  const [inputValue, setInputValue] = useState(params[KEYS.SEARCH] || "");
  const [stores, setStores] = useState<ApiResponse<any[]>>({
    data: [],
    status: 200,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] =
    useState<MonthlyCheckStoreResultDetail | null>(null);
  const [currentCheckboxs, setCurrentCheckboxs] = useState<number[]>([]);
  const debouncedSearch = useDebounce(inputValue.trim(), 500);

  const { control, reset } = useForm({
    defaultValues: {
      comfirmation_date: "",
      theme: "",
      content: "",
      inspection_results: [] as boolean[],
      comments: "",
    },
    resolver: zodResolver(MonthlyInspectionDetailSchema),
  });

  const hasAdminComment = !!detailData?.admin_comment;

  const inspectionResultOptions: CheckboxGroupOption[] = useMemo(() => {
    if (!detailData?.templates_with_answers?.length) return [];

    return detailData.templates_with_answers.map((template) => {
      const isHiddenComment = !currentCheckboxs.includes(template.id);

      return {
        label: (
          <div className="min-w-0 space-y-2">
            <p className="text-sm font-semibold leading-5.5 break-all">
              点検項目{template.order}
            </p>
            <p className="text-xs leading-4.5 break-all">{template.question}</p>
            <div className="min-h-[18px]">
              {!isHiddenComment && (
                <p className="text-sm leading-4.5 break-all">
                  コメント：{template.comment}
                </p>
              )}
            </div>
          </div>
        ),
      };
    });
  }, [detailData?.templates_with_answers, currentCheckboxs]);

  const handleCurrentCheckboxs = useCallback(
    (checkedValues: boolean[]) => {
      if (!detailData?.templates_with_answers) return;

      const checkedTemplateIds = checkedValues
        .map((isChecked, index) => {
          if (isChecked && detailData.templates_with_answers[index]) {
            return detailData.templates_with_answers[index].id;
          }
          return null;
        })
        .filter((id): id is number => id !== null);

      setCurrentCheckboxs(checkedTemplateIds);
    },
    [detailData?.templates_with_answers],
  );

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setDetailData(null);
    setCurrentCheckboxs([]);
    reset({
      comfirmation_date: "",
      theme: "",
      content: "",
      inspection_results: [],
      comments: "",
    });
  };

  const handleOpenDetail = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);

    try {
      const detail: MonthlyCheckStoreResultDetail =
        await monthlyCheckService.getMonthlyCheckStoreResultDetail(id);
      const inspectionResults = detail.templates_with_answers.map(
        (template) => template.store_answer ?? false,
      );

      setDetailData(detail);
      reset({
        comfirmation_date: detail.answer_date || new Date().toISOString(),
        comments: detail.admin_comment || "",
        content: detail.inspection.description || "",
        inspection_results: inspectionResults,
        theme: detail.inspection.theme || "",
      });
      setCurrentCheckboxs(
        detail.templates_with_answers
          .map((template, index) =>
            inspectionResults[index] ? template.id : null,
          )
          .filter((id): id is number => id !== null),
      );
    } catch {
      toastError("データの取得に失敗しました。");
      handleCloseDetail();
    } finally {
      setDetailLoading(false);
    }
  };

  const targetMonthDate = parseDate(params[KEYS.TARGET_MONTH]);
  const totalItems = stores.pagination?.itemCount || 0;

  const updateSearchParams = (object: Record<string, string | undefined>) => {
    const next = { ...params };
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) next[key] = value;
      else delete next[key];
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    monthlyCheckService
      .getMonthlyCheckStoreResults(
        buildListParams(currentPage, debouncedSearch, params, targetMonthDate),
      )
      .then((response) => {
        if (!cancelled) setStores(response);
      })
      .catch(() => {
        if (!cancelled) toastError("データの取得に失敗しました。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    debouncedSearch,
    params[KEYS.ANSWER_STATUS],
    params[KEYS.FROM_DATE],
    params[KEYS.END_DATE],
    params[KEYS.TARGET_MONTH],
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateSearchParams({
      [KEYS.SEARCH]: debouncedSearch || "",
      [KEYS.PAGE]: debouncedSearch ? "1" : params[KEYS.PAGE],
    });
  }, [debouncedSearch]);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const listParams = buildListParams(
        currentPage,
        debouncedSearch,
        params,
        targetMonthDate,
      );
      const blob = await monthlyCheckService.exportMonthlyInspectionsCsv({
        search: listParams.search,
        answer_status: listParams.answer_status,
        answer_date_from: listParams.answer_date_from,
        answer_date_to: listParams.answer_date_to,
        applicable_month: listParams.applicable_month,
        year: listParams.year,
      });
      saveAs(blob, `随時点検_${format(new Date(), "yyyyMMdd")}.csv`);
    } catch {
      toastError("CSVファイルのダウンロードに失敗しました。");
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnType<any>[] = [
    {
      title: "店舗名",
      key: "store_name",
      width: "18%",
      render: (_: unknown, record) => (
        <p
          className="truncate text-other_social_linkedin hover:underline hover:opacity-80 cursor-pointer"
          onClick={() => handleOpenDetail(record.id)}
        >
          {record.store?.name || "-"}
        </p>
      ),
    },
    {
      title: "テーマ",
      dataIndex: "theme",
      key: "theme",
      width: "22%",
      render: (theme: string) => <p className="truncate">{theme || "-"}</p>,
    },
    {
      title: "回答ステータス",
      dataIndex: "answer_status",
      key: "answer_status",
      width: "18%",
      render: (answerStatus: string) => (
        <div className="flex items-center gap-2">
          <span
            className={
              ANSWER_STATUS_STYLE[answerStatus] || ANSWER_STATUS_STYLE.未回答
            }
          />
          <span>{answerStatus || "-"}</span>
        </div>
      ),
    },
    {
      title: "回答日",
      dataIndex: "answer_date",
      key: "answer_date",
      width: "18%",
      render: (date: string | null) => (
        <p className="truncate">{date ? formatToJapaneseDate(date) : "-"}</p>
      ),
    },
    {
      title: "対象年月",
      key: "monthly_target",
      width: "14%",
      render: (_: unknown, record) => (
        <p className="truncate">{getMonthlyTarget(record)}</p>
      ),
    },
  ];

  const hasPagination = stores.data.length > 0;

  return (
    <div>
      <p className="py-4 text-lg font-bold leading-7">随時点検</p>

      <div className="flex justify-between items-center gap-2 flex-wrap lg:flex-nowrap">
        <Input
          placeholder="店舗名またテーマで検索"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="lg:max-w-[320px] min-w-[150px] !h-10 placeholder:text-grey-600 pr-4 !pl-[38px] text-sm"
          containerClassName="flex-1"
          classNameIcon="left-4"
          icon={
            <HiOutlineSearch className="w-[14.575px] h-[14.575px] text-action_light_active" />
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              updateSearchParams({ [KEYS.SEARCH]: inputValue });
            }
          }}
        />
        <div className="flex gap-2 items-center flex-wrap lg:flex-nowrap">
          <Select
            placeholder="回答ステータス"
            height="40px"
            width="156px"
            options={ANSWER_STATUS_OPTIONS}
            value={ANSWER_STATUS_OPTIONS.find(
              (o) => o.value === params[KEYS.ANSWER_STATUS],
            )}
            onChange={(option) =>
              updateSearchParams({
                [KEYS.ANSWER_STATUS]: option?.value,
                [KEYS.PAGE]: "1",
              })
            }
            isClearable
          />
          <DateRangePicker
            containerClassName="relative"
            value={{
              start: params[KEYS.FROM_DATE] || "",
              end: params[KEYS.END_DATE] || "",
            }}
            onChange={(range) =>
              updateSearchParams({
                [KEYS.FROM_DATE]: range.start || undefined,
                [KEYS.END_DATE]: range.end
                  ? endOfDay(range.end).toISOString()
                  : undefined,
                [KEYS.PAGE]: "1",
              })
            }
          />
          <DatePicker
            placeholder="対象年月"
            className="!h-10 w-[156px] px-3 !text-sm"
            icon={CalendarIcon}
            value={targetMonthDate}
            onChange={(value) =>
              updateSearchParams({
                [KEYS.TARGET_MONTH]: value ? String(value) : undefined,
                [KEYS.PAGE]: "1",
              })
            }
            showMonthYearPicker
            dateFormat="yyyy/MM"
            popperPlacement="bottom-end"
          />
        </div>
      </div>

      <div className="pt-4">
        <div className="flex justify-end items-center pb-4 h-9">
          <Button
            buttonType="primary"
            className="rounded-lg px-[10px] text-[13px] leading-[22px] flex items-center gap-2"
            onClick={handleExportCsv}
            isLoading={exporting}
          >
            <HiDownload className="size-4" />
            CSVダウンロード
          </Button>
        </div>

        <div className="min-h-[calc(100vh-325px)]">
          <Table
            columns={columns}
            dataSource={stores.data}
            rowKey="id"
            bordered
            loading={loading}
            loadingClassname="h-[calc(100vh-325px-45px)]"
            onRow={(record) => ({
              style: {
                backgroundColor:
                  record.answer_status === "未回答"
                    ? "var(--Gray-Light-Dark)"
                    : "",
              },
            })}
          />
        </div>

        {hasPagination && (
          <p className="text-sm leading-5.5 h-[28px] flex items-center">
            {stores.data.length}/{totalItems}件
          </p>
        )}
        {hasPagination && (
          <Pagination
            currentPage={currentPage}
            totalPages={stores.pagination?.pageCount || 0}
            onPageChange={(page) =>
              updateSearchParams({ [KEYS.PAGE]: page.toString() })
            }
          />
        )}
      </div>

      <Modal
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDetail();
        }}
        title={detailData?.inspection.theme || ""}
        classNameTitle="[&_p]:break-all"
      >
        <div className="p-4 space-y-4">
          <FormDatePicker
            control={control}
            name="comfirmation_date"
            label="回答日"
            icon={CalendarIcon}
            hideIcon={true}
            disabled
          />

          <FormInput control={control} name="theme" label="テーマ" disabled />

          <FormInput control={control} name="content" label="内容" disabled />

          <div className="space-y-2">
            <p className="text-sm leading-[22px] font-semibold">点検結果</p>
            <FormCheckboxGroup
              control={control}
              name="inspection_results"
              options={inspectionResultOptions}
              direction="vertical"
              className="justify-between w-full px-4 py-2"
              disabled
              onAfterChange={(value) => {
                handleCurrentCheckboxs(value);
              }}
            />
          </div>

          {hasAdminComment && (
            <FormInput
              control={control}
              name="comments"
              label="コメント"
              multiline
              disabled
            />
          )}
        </div>

        <div className="flex gap-4 justify-end items-center px-4 py-2 border-t border-grey-300 w-full">
          <Button
            buttonType="primary"
            className="h-12 rounded-lg px-[22px]"
            onClick={handleCloseDetail}
          >
            閉じる
          </Button>
        </div>

        {detailLoading && <Loading />}
      </Modal>
    </div>
  );
};

export { MonthlyCheckList };
